/**
 * useAuditLogs — TanStack Query hooks for the audit log viewer.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getAuditLogs, getDayAuditLogs } from '@/services/auditLogService';
import type { AuditLogFilters } from '@/types';
import type { Audience } from '@/types/auth.types';
import type { DocumentSnapshot } from 'firebase/firestore';
import type { AuditLogPage } from '@/services/auditLogService';

// ─── Query keys ────────────────────────────────────────────────────────────────

export const auditKeys = {
  all:  ['auditLogs'] as const,
  list: (filters: AuditLogFilters) => [
    'auditLogs',
    'list',
    {
      action:   filters.action   ?? null,
      audience: filters.audience ?? null,
      dateFrom: filters.dateFrom?.toISOString() ?? null,
      dateTo:   filters.dateTo?.toISOString()   ?? null,
    },
  ] as const,
  day: (audience: Audience, dayNumber: number) =>
    ['auditLogs', 'day', audience, dayNumber] as const,
};

// ─── Paginated audit log list ──────────────────────────────────────────────────

export function useAuditLogs(filters: AuditLogFilters) {
  return useInfiniteQuery({
    queryKey:         auditKeys.list(filters),
    queryFn:          ({ pageParam }: { pageParam: DocumentSnapshot | null }) =>
                        getAuditLogs(filters, pageParam),
    initialPageParam: null as DocumentSnapshot | null,
    getNextPageParam: (lastPage: AuditLogPage) =>
                        lastPage.hasMore ? lastPage.lastDoc : undefined,
    staleTime: 30 * 1000,
  });
}

// ─── Per-day history ───────────────────────────────────────────────────────────

export function useDayAuditLogs(audience: Audience, dayNumber: number) {
  return useQuery({
    queryKey: auditKeys.day(audience, dayNumber),
    queryFn:  () => getDayAuditLogs(audience, dayNumber),
    staleTime: 60 * 1000,
  });
}
